import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { Box, Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { isEqual } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import {
  COUNTRIES_LIST,
  STATE_LIST,
  TIME_ZONE_LIST,
} from "../../../../constants";
import {
  Address,
  PROVIDER_GROUP_INITIAL_VALUES,
  PROVIDER_GROUP_SCHEMA,
  ProviderGroup,
  ProviderGroupSubmitValues,
} from "../../../../constants/ProviderGroupFormInitialValues";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useFormScrollIntoView } from "../../../../hooks/use-form-scrollInto-view";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useSetCallerId } from "../../../../queries/twilio/use-create-twilio-number";
import { useTenantConfiguration } from "../../../../queries/use-get-tenant-configuration";
import { useInsurancePayerList } from "../../../../queries/use-insurance-payer";
import { useSpecialtiesList } from "../../../../queries/use-specialities-list";
import {
  showToastOnApiCall,
  convertToBase64,
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  unNecessaryBEOfficeHourTrans,
} from "../../../../utils/utils";
import CustomSelect from "../../../CustomSelect";
import CustomTextArea from "../../../CustomTextArea";
import Flex from "../../../Flex";
import FormHeadChip from "../../../FormHeadText";
import Input from "../../../Input";
import LoadingOverlay from "../../../LoadingOverlay";
import MaskInput from "../../../MaskInput";
import MaskMultiSelectScroll from "../../../MaskMultiSelectScroll";
import OutlineCard from "../../../OutlineCard";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";
import WeekTimePicker from "../../../WeekTimePicker";
import DropZone from "../../DropZone";

interface ProviderGroupDrawerProps {
  openProviderForm: boolean;
  handleClose: () => void;
  title:
    | "Add Provider Group"
    | "Edit Provider Group"
    | "Edit Provider Group Profile";
  prefillValues?: ProviderGroup;
  onSubmit: (values: ProviderGroupSubmitValues) => Promise<any>;
}

const ProviderGroupDrawer: React.FC<ProviderGroupDrawerProps> = ({
  openProviderForm,
  handleClose,
  title,
  prefillValues,
  onSubmit,
}) => {
  const { data: TenantConfigurationData } = useTenantConfiguration({});

  const [
    isBillingAddressSameToPhysicalAdd,
    setIsBillingAddressSameToPhysicalAdd,
  ] = useState<boolean>(false);

  const formik = useFormik<ProviderGroup>({
    initialValues: prefillValues ?? {
      ...PROVIDER_GROUP_INITIAL_VALUES,
      timezone: TenantConfigurationData?.data?.timeZone ?? "",
    },
    validationSchema: PROVIDER_GROUP_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnMount: true,
  });

  useFormScrollIntoView(formik);

  const [callerId, setCallerId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [searchString, setSearchString] = useDebounce<string>("");
  const [specialtiesPagination, setSpecialtiesPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: specialtiesList, isFetching: isSpecialtiesFetching } =
    useSpecialtiesList({
      searchString,
      pagination: specialtiesPagination,
    });
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [insurancePagination, setInsurancePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [isErrorForOfficeHr, setIsErrorForOfficeHr] = useState<boolean>(false);
  const { data: insurancePayerList, isFetching: isInsuranceFetching } =
    useInsurancePayerList({
      searchKey,
      pagination: insurancePagination,
    });

  const specialtiesOptions = useMemo(() => {
    const fetched = (specialtiesList?.data?.content ?? []) as {
      [k: string]: string;
    }[];
    const selected = (formik.values.specialities ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [specialtiesList, formik.values.specialities]);

  const insuranceOptions = useMemo(() => {
    const fetched = (insurancePayerList?.data?.content ?? []) as {
      [k: string]: string;
    }[];
    const selected = (formik.values?.acceptedInsurances ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [insurancePayerList, formik.values?.acceptedInsurances]);

  const { mutateAsync: serProviderGroupCallerId } = useSetCallerId();

  function handleSubmit(v: ProviderGroup) {
    const isBillingAddressEmpty =
      !v?.billingAddress ||
      Object.values(v.billingAddress).every((value) => !value);

    const officeHours = unNecessaryBEOfficeHourTrans(v?.officeHours);
    if (isErrorForOfficeHr) {
      showSnackbar(getErrorSnackbar("Please select valid office hours"));
      return;
    }

    setIsLoading(true);
    onSubmit({
      ...v,
      billingAddress: isBillingAddressEmpty ? undefined : v.billingAddress,
      name: v?.name?.trim() ?? "",
      officeHours,
    })
      .then((res) => {
        queryClient.invalidateQueries({
          queryKey: ["provider-group-profile"],
        });
        queryClient.invalidateQueries({
          queryKey: ["provider-group-profile-key"],
        });

        queryClient.invalidateQueries({
          queryKey: ["provider-group-list"],
        });

        showSnackbar(getSuccessSnackbar(res));
        closeAndReset();
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        const errCode = err?.code;

        if (statusCode === 504 || errCode === "ERR_NETWORK") {
          showSnackbar({
            type: "info",
            message: "Provider Group will be created in a few minutes",
          });
          closeAndReset();
          return;
        }

        showSnackbar(getErrorSnackbar(err));
      })
      .finally(() => setIsLoading(false));
  }

  // Physical address may arrive from the API as undefined, so `touched` never
  // nests for it; fall back to submitCount so Save still surfaces every error.
  function physicalAddressError(field: keyof Address) {
    const touched = (formik.touched?.physicalAddress as Address | undefined)?.[
      field
    ];
    if (!touched && formik.submitCount === 0) return false;
    return (formik.errors?.physicalAddress as Address | undefined)?.[field];
  }

  function closeAndReset() {
    formik.resetForm();
    handleClose();
  }

  useEffect(() => {
    setIsBillingAddressSameToPhysicalAdd(
      !!(prefillValues?.billingAddress && prefillValues?.physicalAddress) &&
        isEqual(prefillValues?.physicalAddress, prefillValues?.billingAddress)
    );
  }, [prefillValues]);

  useEffect(() => {
    isBillingAddressSameToPhysicalAdd &&
      formik.setValues((prev) => ({
        ...prev,
        billingAddress:
          prev?.physicalAddress ?? PROVIDER_GROUP_INITIAL_VALUES.billingAddress,
      }));
  }, [formik.values?.physicalAddress]);

  return (
    <SideSheetDrawer
      open={openProviderForm}
      title={title}
      footerActions={
        <Button
          variant="contained"
          type="submit"
          onClick={formik.submitForm}
          // disabled={!formik.isValid || formik.isSubmitting}
        >
          Save
        </Button>
      }
      onClose={closeAndReset}
      w="60vw"
    >
      <LoadingOverlay isLoading={isLoading} />

      <Grid container width={"100%"} mt={0} pt={0} rowSpacing={2}>
        {/* <Box
          padding={0}
          margin={0}
          bgcolor={"yellow"}
          sx={{
            width: "100%",
          }}
        > */}
        {/* ###Do Not Remove Requirement Changes Frequently### */}
        {/* {title !== "Add Provider Group" && (
            <Box mb={"10px"} mt={"10px"}>
              <Text>Status</Text>
              <FormSwitch
                checked={formik.values?.active}
                onChange={(e) =>
                  formik.setFieldValue("active", e.target.checked)
                }
              />
            </Box>
          )} */}
        <Box width={"100%"} paddingBottom="20px">
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={3} sm={4}>
              <Input
                required
                label="Provider Group Name"
                textFieldProps={{
                  placeholder: "Enter Provider Group Name",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "name",
                  value: formik.values.name,
                }}
                error={formik.touched?.name && formik?.errors?.name}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <Input
                required
                label="Sub Domain"
                textFieldProps={{
                  disabled: !!prefillValues?.uuid,
                  placeholder: "Enter Sub Domain",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "subdomain",
                  value: formik.values.subdomain,
                }}
                error={formik.touched?.subdomain && formik?.errors?.subdomain}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <MaskMultiSelectScroll
                optionWithCheckbox
                onSearch={setSearchString}
                label="Speciality Type"
                value={formik.values.specialities}
                options={specialtiesOptions}
                onChange={(val) => formik.setFieldValue("specialities", val)}
                typographyProps={{
                  onBlur: formik.handleBlur,
                }}
                pagination={specialtiesPagination}
                setPagination={setSpecialtiesPagination}
                totalElements={specialtiesList?.data?.page?.totalElements}
                loading={isSpecialtiesFetching}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <Input
                required
                label="Group NPI Number"
                error={
                  (formik.touched?.npi || formik.submitCount > 0) &&
                  formik?.errors?.npi
                }
                textFieldProps={{
                  placeholder: "Enter Group NPI Number",
                  onChange: (e) =>
                    formik.setFieldValue(
                      "npi",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    ),
                  onBlur: formik.handleBlur,
                  name: "npi",
                  value: formik.values.npi ?? "",
                  inputProps: {
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                  helperText: (
                    <Box
                      component="span"
                      sx={{ display: "block", textAlign: "right" }}
                    >
                      {`${(formik.values.npi ?? "").length}/10`}
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <Input
                required
                label="Federal Tax ID"
                textFieldProps={{
                  placeholder: "Enter Federal Tax ID",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "federalTaxId",
                  value: formik.values.federalTaxId ?? "",
                }}
                error={
                  (formik.touched?.federalTaxId || formik.submitCount > 0) &&
                  formik?.errors?.federalTaxId
                }
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <MaskInput
                mask={"(999) 999-9999"}
                label="Contact Number"
                name="phone"
                error={formik.errors?.phone}
                placeholder="Enter Contact Number"
                value={formik.values.phone}
                onChange={(e) => formik.setFieldValue("phone", e.target.value)}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <Input
                label="Email"
                textFieldProps={{
                  placeholder: "Enter Email",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "email",
                  value: formik.values.email,
                }}
                error={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              {/* <Input
                label="Fax ID"
                textFieldProps={{
                  placeholder: "Enter Fax ID",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  value: formik.values.fax,
                  name: "fax",
                }}
              /> */}
              <MaskInput
                mask={"(999) 999-9999"}
                label="Fax ID"
                name="fax"
                error={formik.errors?.fax}
                placeholder="Enter Fax ID"
                value={formik.values.fax}
                onChange={(e) => formik.setFieldValue("fax", e.target.value)}
              />
            </Grid>
            <Grid item xs={2.8} sm={4}>
              <MaskMultiSelectScroll
                optionWithCheckbox
                label="Insurance(s) Accepted"
                value={formik.values?.acceptedInsurances}
                onChange={(val) =>
                  formik.setFieldValue("acceptedInsurances", val)
                }
                onSearch={setSearchKey}
                options={insuranceOptions}
                pagination={insurancePagination}
                setPagination={setInsurancePagination}
                totalElements={insurancePayerList?.data?.page?.totalElements}
                loading={isInsuranceFetching}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <CustomSelect
                required
                label="Time Zone"
                name="timezone"
                value={(formik.values as any).timezone}
                options={TIME_ZONE_LIST}
                onChange={(val) => formik.setFieldValue("timezone", val)}
                handleBlur={formik.handleBlur}
                error={formik.touched?.timezone && formik?.errors?.timezone}
              />
            </Grid>
            <Grid item xs={3} sm={4}>
              <Input
                label="Website"
                textFieldProps={{
                  placeholder: "Enter Website",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "website",
                  value: formik.values.website,
                }}
                error={formik.touched.website && formik.errors.website}
              />
            </Grid>
            {!!prefillValues && (
              <Grid item xs={3} sm={4}>
                <MaskInput
                  mask={"(999) 999-9999"}
                  label="Caller Id"
                  placeholder="Enter Caller Id"
                  onChange={(e) => setCallerId(e.target.value)}
                  value={callerId}
                  // error={formik.touched.website && formik.errors.website}
                />
              </Grid>
            )}
            {!!prefillValues && (
              <Grid item xs={3} sm={4}>
                <Button
                  // sx={{ marginTop: "30px" }}
                  variant="contained"
                  onClick={() => {
                    if (callerId)
                      showToastOnApiCall({
                        apiCall: serProviderGroupCallerId({
                          twilioCallerId: { caller_id: callerId },
                          providerGroupId: formik.values?.uuid ?? "",
                          xTENANTID: getXtenantId(),
                        }),
                        showSnackbar: showSnackbar,
                      });
                  }}
                  disabled={!callerId?.trim()}
                >
                  Set Caller Id
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <CustomTextArea
                label="Information"
                id="outlined-multiline-static"
                rows={3}
                variant="outlined"
                name="description"
                placeholder="Enter Information"
                onBlur={formik.handleBlur}
                value={formik.values?.description}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </Box>
        {/* </Box> */}
        <Box
          sx={{
            marginTop: "10px",
            width: "100%",
          }}
        >
          <FormHeadChip mb={"10px"}>Physical Address</FormHeadChip>
          <OutlineCard>
            <Box padding="10px">
              <Grid container>
                <Grid container columnSpacing={3}>
                  <Grid item xs={6}>
                    <Input
                      required
                      label="Address Line 1"
                      textFieldProps={{
                        placeholder: "Address Line 1",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "physicalAddress.line1",
                        value: formik.values?.physicalAddress?.line1 ?? "",
                      }}
                      error={physicalAddressError("line1")}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      label="Address Line 2"
                      textFieldProps={{
                        placeholder: "Address Line 2",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "physicalAddress.line2",
                        value: formik.values?.physicalAddress?.line2,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2} columnSpacing={3}>
                  <Grid item xs={3}>
                    <Input
                      required
                      label="City"
                      textFieldProps={{
                        placeholder: "City",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "physicalAddress.city",
                        value: formik.values?.physicalAddress?.city,
                      }}
                      error={physicalAddressError("city")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      required
                      label="State"
                      placeholder="State"
                      options={STATE_LIST}
                      value={formik.values?.physicalAddress?.state}
                      onChange={(v) =>
                        formik.setFieldValue("physicalAddress.state", v)
                      }
                      error={physicalAddressError("state")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      required
                      label="Country"
                      placeholder="Country"
                      disableSortOptions
                      options={COUNTRIES_LIST}
                      value={formik.values?.physicalAddress?.country}
                      onChange={(v) =>
                        formik.setFieldValue("physicalAddress.country", v)
                      }
                      error={physicalAddressError("country")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      required
                      label="ZIP Code"
                      textFieldProps={{
                        name: "physicalAddress.zipcode",
                        value: (() => {
                          const d = (
                            formik.values.physicalAddress?.zipcode || ""
                          )
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          return d.length > 5
                            ? `${d.slice(0, 5)}-${d.slice(5)}`
                            : d;
                        })(),
                        onChange: (e) => {
                          const digits = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          const val =
                            digits.length > 5
                              ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                              : digits;
                          formik.setFieldValue("physicalAddress.zipcode", val);
                        },
                        onBlur: formik.handleBlur,
                        inputProps: { maxLength: 10 },
                      }}
                      error={physicalAddressError("zipcode")}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </OutlineCard>
        </Box>

        <Box
          sx={{
            marginTop: "10px",
            width: "100%",
          }}
        >
          <Flex mt={"10px"}>
            <FormHeadChip my={"10px"}>Billing Address</FormHeadChip>
            <FormControlLabel
              value="end"
              control={
                <Checkbox
                  checked={isBillingAddressSameToPhysicalAdd}
                  onChange={(e) => {
                    /** if checked is true
                     * set copied the physical to billing add
                     * if checked is false
                     * set initial billing
                     */
                    formik.setValues((prev) => ({
                      ...prev,
                      billingAddress: e.target.checked
                        ? { ...prev.physicalAddress }
                        : PROVIDER_GROUP_INITIAL_VALUES.billingAddress,
                    }));

                    setIsBillingAddressSameToPhysicalAdd(e.target.checked);
                  }}
                />
              }
              label={<Text>Same as Physical Address</Text>}
              labelPlacement="end"
              sx={{ marginLeft: "10px" }}
            />
          </Flex>
          <OutlineCard>
            <Box padding="10px">
              <Grid container>
                <Grid container columnSpacing={3}>
                  <Grid item xs={6}>
                    <Input
                      label="Address Line 1"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "Address Line 1",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.line1",
                        value: formik.values?.billingAddress?.line1 ?? "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      label="Address Line 2"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "Address Line 2",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.line2",
                        value: formik.values?.billingAddress?.line2 ?? "",
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2} columnSpacing={3}>
                  <Grid item xs={3}>
                    <Input
                      label="City"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "City",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.city",
                        value: formik.values?.billingAddress?.city ?? "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      readOnly={isBillingAddressSameToPhysicalAdd}
                      label="State"
                      placeholder="State"
                      options={STATE_LIST}
                      value={formik.values?.billingAddress?.state}
                      onChange={(v) =>
                        formik.setFieldValue("billingAddress.state", v)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      readOnly={isBillingAddressSameToPhysicalAdd}
                      label="Country"
                      placeholder="Country"
                      disableSortOptions
                      options={COUNTRIES_LIST}
                      value={formik.values?.billingAddress?.country}
                      onChange={(v) =>
                        formik.setFieldValue("billingAddress.country", v)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="ZIP Code"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        name: "billingAddress.zipcode",
                        value: (() => {
                          const d = (
                            formik.values.billingAddress?.zipcode ?? ""
                          )
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          return d.length > 5
                            ? `${d.slice(0, 5)}-${d.slice(5)}`
                            : d;
                        })(),
                        onChange: (e) => {
                          const digits = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          const val =
                            digits.length > 5
                              ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                              : digits;
                          formik.setFieldValue("billingAddress.zipcode", val);
                        },
                        onBlur: formik.handleBlur,
                        inputProps: { maxLength: 10 },
                      }}
                      error={
                        (formik?.touched?.billingAddress as any)?.zipcode &&
                        (formik.errors?.billingAddress as any)?.zipcode
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </OutlineCard>
        </Box>

        <Grid item xs={3}>
          <Box marginY={"20px"}>
            <FormHeadChip my={"10px"}>Logo</FormHeadChip>
            <DropZone
              supportedFile=".jpg,.jpeg,.png,.svg"
              value={formik.values?.avatar}
              backgroundColor={"primary.light"}
              content="Upload Provider Group Logo"
              height={"190px"}
              uploadIcon={
                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "3rem" }} />
              }
              onClick={async (files) => {
                if (files) {
                  const base64File = await convertToBase64(files[0]);
                  formik.setFieldValue("avatar", base64File);
                } else formik.setFieldValue("avatar", null);
              }}
              accept=".jpg,.png,.svg"
            />
          </Box>
        </Grid>
        <Box width={"100%"}>
          <FormHeadChip mb={"10px"}>Provider Group Office Time</FormHeadChip>
          <OutlineCard>
            <WeekTimePicker
              setIsError={setIsErrorForOfficeHr}
              pickedTimes={formik.values?.officeHours}
              onChange={(value) => formik.setFieldValue("officeHours", value)}
            />
          </OutlineCard>
        </Box>
      </Grid>
    </SideSheetDrawer>
  );
};

export default ProviderGroupDrawer;
